var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthenticated, useGetResourceLabel, useNotify, useRedirect, useResourceContext, useResourceDefinition, useTranslate, } from 'react-admin';
import { useQueryClient } from 'react-query';
import { useAddRootNode, useAddChildNode } from '../dataProvider';
import { useTreeContext } from './useTreeContext';
/**
 * Prepare data for the Create view
 *
 * @param {Object} props The props passed to the Create component.
 *
 * @return {Object} controllerProps Fetched data and callbacks for the Create view
 *
 * @example
 *
 * import { CreateView } from 'react-admin';
 * import { useCreateNodeController } from '@react-admin/ra-tree';
 *
 * const MyCreateNode = props => {
 *     const controllerProps = useCreateNodeController(props);
 *     return <CreateView {...controllerProps} {...props} />;
 * }
 */
export var useCreateNodeController = function (props) {
    var disableAuthentication = props.disableAuthentication, _a = props.record, record = _a === void 0 ? {} : _a, redirectTo = props.redirect, _b = props.mutationOptions, mutationOptions = _b === void 0 ? {} : _b, transform = props.transform;
    useAuthenticated({ enabled: !disableAuthentication });
    var resource = useResourceContext(props);
    var getResourceLabel = useGetResourceLabel();
    var queryClient = useQueryClient();
    var location = useLocation();
    var handleAddNode = useTreeContext().handleAddNode;
    var parentId = location.state && location.state.parentId;
    var isChild = typeof parentId !== 'undefined' && parentId !== null;
    var recordToUse = (location.state && location.state.record) ||
        record;
    var onSuccess = mutationOptions.onSuccess, onError = mutationOptions.onError, otherMutationOptions = __rest(mutationOptions, ["onSuccess", "onError"]);
    var _c = useResourceDefinition(props), hasEdit = _c.hasEdit, hasShow = _c.hasShow;
    var finalRedirectTo = redirectTo || getDefaultRedirectRoute(hasShow, hasEdit);
    var _d = useAddRootNode(resource, undefined, otherMutationOptions), createRoot = _d[0], createRootState = _d[1];
    var _e = useAddChildNode(resource, undefined, otherMutationOptions), createChild = _e[0], createChildState = _e[1];
    // show a local new node in the tree on mount, remove it on unmount
    useEffect(function () {
        handleAddNode({
            record: {},
            parentId: parentId,
        });
        return function () {
            handleAddNode(undefined);
        };
    }, [handleAddNode, parentId]);
    var translate = useTranslate();
    var notify = useNotify();
    var redirect = useRedirect();
    var save = useCallback(function (data, _a) {
        var _b = _a === void 0 ? {} : _a, onSuccessFromSave = _b.onSuccess, onErrorFromSave = _b.onError, transformFromSave = _b.transform;
        var defaultOnSuccess = function (newNode) {
            notify('ra.notification.created', {
                type: 'info',
                messageArgs: {
                    smart_count: 1,
                },
            });
            redirect(finalRedirectTo, resource, newNode.id, newNode);
            queryClient.refetchQueries([resource, 'getTree'], {
                active: true,
            });
            queryClient.refetchQueries([resource, 'getRootNodes'], {
                active: true,
            });
        };
        var defaultOnError = function (error) {
            notify(typeof error === 'string'
                ? error
                : error.message || 'ra.notification.http_error', { type: 'warning' });
        };
        return Promise.resolve(transformFromSave
            ? transformFromSave(data)
            : transform
                ? transform(data)
                : data).then(function (data) {
            var options = {
                onSuccess: function (data, variables, context) {
                    handleAddNode(undefined);
                    onSuccessFromSave
                        ? onSuccessFromSave(data, variables, context)
                        : onSuccess
                            ? onSuccess(data, variables, context)
                            : defaultOnSuccess(data);
                },
                onError: onErrorFromSave
                    ? onErrorFromSave
                    : onError
                        ? onError
                        : defaultOnError,
            };
            if (isChild) {
                return createChild(resource, { data: data, parentId: parentId, meta: otherMutationOptions.meta }, options);
            }
            else {
                return createRoot(resource, { data: data, meta: otherMutationOptions.meta }, options);
            }
        });
    }, [
        createChild,
        createRoot,
        handleAddNode,
        isChild,
        notify,
        onError,
        onSuccess,
        parentId,
        queryClient,
        redirect,
        resource,
        transform,
        otherMutationOptions.meta,
        finalRedirectTo,
    ]);
    var resourceName = getResourceLabel(resource, 1);
    var defaultTitle = translate('ra.page.create', {
        name: "".concat(resourceName),
    });
    return {
        isLoading: false,
        isFetching: false,
        defaultTitle: defaultTitle,
        save: save,
        saving: createRootState.isLoading || createChildState.isLoading,
        resource: resource,
        record: recordToUse,
        redirect: finalRedirectTo,
    };
};
var getDefaultRedirectRoute = function (hasShow, hasEdit) {
    if (hasEdit) {
        return 'edit';
    }
    if (hasShow) {
        return 'show';
    }
    return 'list';
};
